import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Badge,
} from "@mui/material";
import NorthOutlinedIcon from "@mui/icons-material/NorthOutlined";
import AddIcon from "@mui/icons-material/Add";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockOutlineIcon from "@mui/icons-material/LockOutline";
import SouthIcon from "@mui/icons-material/South";
import urlConstant from "../../constants/urlConstants";
import { httpPost } from "../../services/commonService";
import dataConstant from "../../constants/dataConstants";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";

const CustomTextFieldInsideLabel = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem",
    color: "var(--black)",
    marginBottom: theme.spacing(0.5),
  },
}));

let accountID = 0;

const BinaryPlacementTree = () => {
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const [jumpToId, setJumpToId] = useState("");
  const [legPlacementData, setLegPlacementData] = useState(null);
  const [rightPlacementData, setRightPlacementData] = useState(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [state, setState] = useState({
    binaryPlacementTreeList: [],
    treeData: [],
    moveUpOneLevel: true,
    refId: "",
    acctId: 0,
    secondLevelData: [],
    isDisplay: false,
    thirdLevelData: [],
  });

  useEffect(() => {
    const getBinaryPlacementTreeURL =
      urlConstant.BinaryPlacementTree.BinaryPlacementTreeList;
    httpPost(
      getBinaryPlacementTreeURL,
      {},
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value;
        const treeData = {};
        _.each(responseData, function (obj) {
          treeData[obj.PlacementName] = obj;
          return { treeData };
        });
        const getSecondLevel = Object.values(treeData).filter(
          (item) => item.PlacementName === "2" || item.PlacementName === "3"
        );
        const getThirdLevel = Object.values(treeData).filter(
          (item) =>
            item.PlacementName === "4" ||
            item.PlacementName === "5" ||
            item.PlacementName === "6" ||
            item.PlacementName === "7"
        );
        setState((prevState) => ({
          ...prevState,
          binaryPlacementTreeList: responseData,
          refId: responseData[0].RepSiteName,
          acctId: responseData[0].AcctID,
          treeData: treeData,
          isDisplay: true,
          secondLevelData: getSecondLevel,
          thirdLevelData: getThirdLevel,
        }));
        accountID = responseData[0].AcctID;
        getBinaryStatisticData(0);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const getBinaryStatisticData = (acctId) => {
    const model = {
      AcctID: acctId,
    };
    httpPost(
      urlConstant.BinaryPlacementTree.GetPlacementTreeStatistics,
      model,
      true,
      false,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value;
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          const getLegData = _.find(
            responseData.BinaryPlacementTreeStatisticData,
            (item) => item.LegType === "L"
          );
          const getRightData = _.find(
            responseData.BinaryPlacementTreeStatisticData,
            (item) => item.LegType === "R"
          );
          setLegPlacementData(getLegData);
          setRightPlacementData(getRightData);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const setTreeData = (e) => {
    const treeData = {};
    _.each(e, function (obj) {
      treeData[obj.PlacementName] = obj;
      return { treeData };
    });
    const getSecondLevel = Object.values(treeData).filter(
      (item) => item.PlacementName === "2" || item.PlacementName === "3"
    );
    const getThirdLevel = Object.values(treeData).filter(
      (item) =>
        item.PlacementName === "4" ||
        item.PlacementName === "5" ||
        item.PlacementName === "6" ||
        item.PlacementName === "7"
    );
    setState((prevState) => ({
      ...prevState,
      treeData,
      secondLevelData: getSecondLevel,
      thirdLevelData: getThirdLevel,
    }));
  };

  const searchTree = () => {
    if (jumpToId !== "") {
      setState((prevState) => ({
        ...prevState,
        isDisplay: false,
      }));
      const model = {
        TreeAcctID: state.acctId,
        AcctID: jumpToId,
      };
      const getBinaryPlacementTreeURL =
        urlConstant.BinaryPlacementTree.CheckAccountIdInPlacementTree;
      httpPost(
        getBinaryPlacementTreeURL,
        model,
        true,
        true,
        accessToken,
        impersonateEmail
      )
        .then((response) => {
          const responseData = response.Value;
          if (response.Status === dataConstant.APIResponseStatus.Failure) {
            setState((prevState) => ({
              ...prevState,
              isDisplay: true,
            }));
            showSnackbar(response?.error, "error");
          } else {
            setState((prevState) => ({
              ...prevState,
              binaryPlacementTreeList: responseData.placementTreeList.Data,
              moveUpOneLevel: false,
              isDisplay: true,
            }));
            setTreeData(responseData.placementTreeList.Data);
            getBinaryStatisticData(jumpToId);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const getTopLevel = () => {
    if (
      (state.treeData && state.treeData["1"]
        ? state.treeData["1"].AcctID
        : null) !== state.acctId
    ) {
      setState((prevState) => ({
        ...prevState,
        isDisplay: false,
      }));
      const getBinaryPlacementTreeURL =
        urlConstant.BinaryPlacementTree.BinaryPlacementTreeList;
      httpPost(
        getBinaryPlacementTreeURL,
        {},
        true,
        true,
        accessToken,
        impersonateEmail
      )
        .then((response) => {
          const responseData = response.Value;
          setState((prevState) => ({
            ...prevState,
            binaryPlacementTreeList: responseData,
            moveUpOneLevel: true,
            isDisplay: true,
          }));
          setTreeData(responseData);
          getBinaryStatisticData(0);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const getUpOneLevel = () => {
    setState((prevState) => ({
      ...prevState,
      isDisplay: false,
    }));
    const model = {
      AcctID:
        state.treeData && state.treeData["1"]
          ? state.treeData["1"].PlacementID
          : null,
    };
    let moveUpOneLevel = false;
    if (
      (state.treeData && state.treeData["1"]
        ? state.treeData["1"].PlacementID
        : null) === state.acctId
    ) {
      moveUpOneLevel = true;
    }
    const getBinaryPlacementTreeURL =
      urlConstant.BinaryPlacementTree.BinaryPlacementTreeList;
    httpPost(
      getBinaryPlacementTreeURL,
      model,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value;
        setState((prevState) => ({
          ...prevState,
          binaryPlacementTreeList: responseData,
          moveUpOneLevel,
          isDisplay: true,
        }));
        setTreeData(responseData);
        getBinaryStatisticData(model.AcctID === null ? 0 : model.AcctID);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getUserTree = (e) => {
    const model = {
      AcctID:
        state.treeData && state.treeData[e] ? state.treeData[e].AcctID : null,
    };
    setState((prevState) => ({
      ...prevState,
      isDisplay: false,
    }));
    const getBinaryPlacementTreeURL =
      urlConstant.BinaryPlacementTree.BinaryPlacementTreeList;
    httpPost(
      getBinaryPlacementTreeURL,
      model,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value;
        setState((prevState) => ({
          ...prevState,
          binaryPlacementTreeList: responseData,
          moveUpOneLevel: false,
          isDisplay: true,
        }));
        setTreeData(responseData);
        getBinaryStatisticData(model.AcctID === null ? 0 : model.AcctID);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getBottomUserTree = (e) => {
    const model = {
      AcctID: accountID,
      PlacementLeg: e,
    };
    setState((prevState) => ({
      ...prevState,
      isDisplay: false,
    }));
    const getBottomPlacementTreeURL =
      urlConstant.BinaryPlacementTree.GetBottomPlacementTree;
    httpPost(
      getBottomPlacementTreeURL,
      model,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          const responseData = response.Value;
          setState((prevState) => ({
            ...prevState,
            binaryPlacementTreeList: responseData,
            moveUpOneLevel: false,
            isDisplay: true,
          }));
          setTreeData(responseData);
          getBinaryStatisticData(response.Value[0]?.AcctID);
        } else if (response.Status === dataConstant.APIResponseStatus.Failure) {
          showSnackbar(response?.error, "error");
          setState((prevState) => ({
            ...prevState,
            isDisplay: true,
          }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return state.isDisplay ? (
    <>
      <Box width={"100%"}>
        <Typography
          color="secondary.light"
          display={{ xs: "block", md: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
          mb={"20px"}
        >
          {t("My Team")}
        </Typography>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Stack gap={"20px"}>
            <Typography color="secondary.light" variant="h4" fontWeight={600}>
              {t("BinaryPlacementTree.BinaryPlacementTree")}
            </Typography>

            <Card
              sx={(theme) => ({
                p: "20px",
                background: theme.palette.common.black,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                boxShadow: "none",
              })}
            >
              <Grid container spacing={2} width={"100%"}>
                <Grid
                  sx={{ order: { xs: 2, sm: 1 } }}
                  size={{ xs: 12, md: 6, lg: 4 }}
                >
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      background: theme.palette.background.cardGradient,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                      boxShadow: "none",
                    })}
                  >
                    <Stack
                      gap={1.5}
                      display={"flex"}
                      justifyContent={"center"}
                      textAlign={"center"}
                      direction={"row"}
                      alignItems={"center"}
                      width={"100%"}
                    >
                      <Typography
                        color="common.white"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {t("Dashboard.Left")}
                      </Typography>
                      <img src="/icons/left-tree.svg" alt="user" />
                    </Stack>
                    <Stack textAlign={"center"}>
                      <Typography
                        color="common.light"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {legPlacementData?.TotalMembers ?? 0}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{}}
                        variant="body2"
                        fontWeight={600}
                      >
                        {t("Common.member")}
                      </Typography>
                    </Stack>
                    <TableContainer
                      sx={{ background: "transparent", boxShadow: "none" }}
                      component={Paper}
                    >
                      <Table
                        sx={{ background: "transparent" }}
                        aria-label="simple table"
                      >
                        <TableBody>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FF6C6C",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Inactive accounts
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.InactiveCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#58B84A",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Affiliates
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.AffiliateCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FFB752",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  CORE
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveBYLDerCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#6D9AE2",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Consultants
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveConsultantCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#ee5d2c",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Aventi Travel Business
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveAventiTravelBusinessCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#283841",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Aventi Travel
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveAventiTravelCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#e6e608",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  ELITE
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveOmniPlatinumCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FFF",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  PREMIER
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {legPlacementData?.ActiveOmniDiamondCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>
                <Grid
                  sx={{ order: { xs: 1, sm: 2 } }}
                  size={{ xs: 12, md: 6, lg: 4 }}
                >
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      background: theme.palette.common.black,
                      borderRadius: "6px",
                      backgroundImage: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: "20px",
                      boxShadow: "none",
                      height: "100%",
                    })}
                  >
                    <Stack textAlign={"center"}>
                      <Typography
                        color="secondary.light"
                        sx={{}}
                        variant="body1"
                        fontWeight={600}
                      >
                        {state.treeData && state.treeData["1"]
                          ? state.treeData["1"].AcctID
                          : null}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{}}
                        variant="body2"
                      >
                        {t("PersonallySponsored.Account ID")}
                      </Typography>
                    </Stack>
                    <Stack textAlign={"center"}>
                      <Typography
                        color="secondary.light"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {state.treeData && state.treeData["1"]
                          ? state.treeData["1"].RepSiteName
                          : null}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{}}
                        variant="body2"
                      >
                        {t("Common.SiteName")}
                      </Typography>
                    </Stack>
                    <Stack
                      textAlign={"center"}
                      direction={{ xs: "column", sm: "row" }}
                      gap={1}
                      justifyContent={"space-between"}
                      width={"50%"}
                      alignItems={{ xs: "center", sm: "flex-end" }}
                    >
                      <Stack textAlign={"center"}>
                        <Typography
                          color="secondary.light"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {state.treeData && state.treeData["1"]
                            ? state.treeData["1"].achievedRankID
                            : null}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{}}
                          variant="body2"
                        >
                          {t("Common.RankAchieved")}
                        </Typography>
                      </Stack>
                      <Stack textAlign={"center"}>
                        <Typography
                          color="secondary.light"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {state.treeData && state.treeData["1"]
                            ? state.treeData["1"].RankID
                              ? state.treeData["1"].RankID
                              : "-"
                            : null}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{}}
                          variant="body2"
                        >
                          {t("PersonallySponsored.Rank")}
                        </Typography>
                      </Stack>
                      <Stack textAlign={"center"} display={"none"}>
                        <Typography
                          color="secondary.light"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {state.treeData && state.treeData["1"]
                            ? state.treeData["1"].RefName
                              ? state.treeData["1"].RefName
                              : "-"
                            : null}
                        </Typography>
                        <Typography
                          color="secondary.light"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {state.treeData &&
                          state.treeData["1"] &&
                          state.treeData["1"].RefID
                            ? "(" + state.treeData["1"].RefID + ")"
                            : null}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{}}
                          variant="body2"
                        >
                          {t("Common.Referred")}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
                <Grid sx={{ order: { xs: 3 } }} size={{ xs: 12, md: 6, lg: 4 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      background: theme.palette.background.cardGradient,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                      boxShadow: "none",
                    })}
                  >
                    <Stack
                      gap={1.5}
                      display={"flex"}
                      justifyContent={"center"}
                      textAlign={"center"}
                      direction={"row"}
                      alignItems={"center"}
                      width={"100%"}
                    >
                      <img src="/icons/right-tree.svg" alt="user" />
                      <Typography
                        color="common.white"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {t("BinaryPlacementTree.Right")}
                      </Typography>
                    </Stack>
                    <Stack textAlign={"center"}>
                      <Typography
                        color="common.light"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {rightPlacementData?.TotalMembers ?? 0}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{}}
                        variant="body2"
                        fontWeight={600}
                      >
                        {t("Common.member")}
                      </Typography>
                    </Stack>
                    <TableContainer
                      sx={{ background: "transparent", boxShadow: "none" }}
                      component={Paper}
                    >
                      <Table
                        sx={{ background: "transparent" }}
                        aria-label="simple table"
                      >
                        <TableBody>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FF6C6C",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Inactive accounts
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.InactiveCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#58B84A",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Affiliates
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.AffiliateCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FFB752",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  CORE
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveBYLDerCount ?? "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#6D9AE2",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Consultants
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveConsultantCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#ee5d2c",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Aventi Travel Business
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveAventiTravelBusinessCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#283841",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  Aventi Travel
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveAventiTravelCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#e6e608",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  ELITE
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveOmniPlatinumCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "& td,& th": {
                                borderBottom:
                                  "1px solid var(--black) !important",
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Badge
                                  sx={{
                                    backgroundColor: "#FFF",
                                    height: "7px",
                                    width: "7px",
                                    overflow: "hidden",
                                    borderRadius: "50%",
                                  }}
                                ></Badge>
                                <Typography
                                  color="secondary.main"
                                  sx={{ fontSize: "12px" }}
                                  variant="body2"
                                >
                                  PREMIER
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell valign="middle" align="right">
                              <Typography
                                color="common.white"
                                sx={{}}
                                variant="body1"
                                fontWeight={600}
                              >
                                {rightPlacementData?.ActiveOmniDiamondCount ??
                                  "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>
              </Grid>
            </Card>
            <Card
              sx={(theme) => ({
                p: "20px",
                background: theme.palette.common.black,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                boxShadow: "none",
                width: "100%",
                flexDirection: { xs: "column-reverse", sm: "row" },
              })}
            >
              <Stack
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                }}
                gap={2}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => getTopLevel()}
                  startIcon={<NorthOutlinedIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {t("BinaryPlacementTree.GoToTopLevel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => getUpOneLevel()}
                  disabled={state.moveUpOneLevel}
                  color="secondary"
                  startIcon={<NorthOutlinedIcon />}
                >
                  {t("BinaryPlacementTree.MoveUpOneLevel")}
                </Button>
              </Stack>
              <Stack direction={"row"} gap={2}>
                <CustomTextFieldInsideLabel
                  className="cm-field-nolabel-focus"
                  fullWidth
                  label={t("BinaryPlacementTree.Jump To ID")}
                  onChange={(e) => setJumpToId(e.target.value)}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  onClick={() => searchTree()}
                  color="secondary"
                  sx={{ px: 3 }}
                >
                  {t("BinaryPlacementTree.Go")}
                </Button>
              </Stack>
            </Card>
            <Stack>
              <Stack>
                {state?.treeData?.["1"]?.IsLock ? (
                  // If locked, show only the lock icon centered
                  <Box className="cm-box cm-box-disabled">
                    <Box className="cm-title" mb={1}>
                      <Typography component="span" />
                    </Box>
                    <Box className="locked" textAlign="center">
                      <LockOutlineIcon sx={{ fontSize: "2.5rem" }} />
                    </Box>
                  </Box>
                ) : (
                  // Else, show all the data exactly as before
                  <>
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.secondary.lightest,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        boxShadow: "none",
                        width: "100%",
                        flexDirection: "column",
                      })}
                    >
                      <Stack
                        sx={{
                          position: "relative",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Paper
                          sx={(theme) => ({
                            p: "5px",
                            background: theme.palette.common.black,
                            position: "absolute",
                            top: "0",
                            left: "0",
                          })}
                        >
                          <Typography variant="body2" color="secondary.light">
                            {state?.treeData?.["1"]?.PlacementName ||
                              "User Name"}
                          </Typography>
                        </Paper>
                        <Paper
                          sx={{
                            boxShadow: "none",
                            background: "none",
                            borderRadius: "50%",
                            overflow: "hidden",
                            width: 70,
                            height: 70,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid var(--secondary-main)",
                          }}
                        >
                          <img
                            width={70}
                            height={70}
                            alt="profile-img"
                            src={
                              state?.treeData?.["1"]?.ProfilePicPath !== null
                                ? state?.treeData?.["1"]?.ProfilePicPath
                                : "https://byldstorageprod.blob.core.windows.net/byldcontainer-prod/backoffice-assets/default-user.png"
                            }
                            style={{
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        </Paper>
                      </Stack>
                      <Stack
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          textAlign: "center",
                          background: "var(--black)",
                        }}
                      >
                        <Stack textAlign={"center"} sx={{ padding: "5px" }}>
                          <Typography
                            color="secondary.light"
                            variant="body1"
                            fontWeight={600}
                            className="cm-tree-name"
                          >
                            {state?.treeData?.["1"]?.AcctName || "User Name"}
                          </Typography>
                          <Typography color="secondary.text" variant="body2">
                            ({state?.treeData?.["1"]?.AcctID || "----"})
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack width={"100%"}>
                        <Stack
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            textAlign: "center",
                          }}
                        >
                          <Stack textAlign={"center"} sx={{ padding: "5px" }}>
                            <Typography
                              color="secondary.text"
                              variant="body2"
                              fontWeight={300}
                              className="cm-placement-consult"
                            >
                              {state?.treeData?.["1"]?.RankName}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            textAlign: "center",
                            justifyContent: "space-around",
                          }}
                        >
                          <Stack textAlign={"center"} sx={{ padding: "5px" }}>
                            <Typography
                              color="secondary.light"
                              variant="h4"
                              fontWeight={600}
                            >
                              {state?.treeData?.["1"]?.LeftLegVol != null
                                ? state.treeData["1"].LeftLegVol.toLocaleString(
                                    "en-US"
                                  )
                                : "0"}
                            </Typography>
                            <Typography color="secondary.text" variant="body2">
                              {t("BinaryPlacementTree.Left")}
                            </Typography>
                          </Stack>
                          <Stack textAlign={"center"} sx={{ padding: "5px" }}>
                            <Typography
                              color="secondary.light"
                              variant="h4"
                              fontWeight={600}
                            >
                              {state?.treeData?.["1"]?.RightLegVol != null
                                ? state.treeData[
                                    "1"
                                  ].RightLegVol.toLocaleString("en-US")
                                : "0"}
                            </Typography>
                            <Typography color="secondary.text" variant="body2">
                              {t("BinaryPlacementTree.Right")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                    <Stack alignItems={"center"}>
                      <Card
                        sx={{
                          width: "2px",
                          height: "15px",
                          background: "var(--headerbg)",
                        }}
                      ></Card>
                      <Card
                        sx={{
                          width: "45%",
                          height: "15px",
                          borderTop: "2px solid var(--headerbg)",
                          borderLeft: "2px solid var(--headerbg)",
                          borderRight: "2px solid var(--headerbg)",
                          background: "transparent",
                          boxShadow: "none",
                        }}
                      ></Card>
                    </Stack>
                  </>
                )}
              </Stack>
              <Grid container spacing={2}>
                {state.secondLevelData?.map((item) => {
                  return (
                    <Grid key={item.PlacementName} size={{ xs: 6, md: 6 }}>
                      {item.State === "Open" ? (
                        <>
                          <Card
                            sx={(theme) => ({
                              p: "20px",
                              background: theme.palette.secondary.lightest,
                              borderRadius: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              boxShadow: "none",
                              width: "100%",
                              flexDirection: "column",
                              minHeight: "285px",

                              [theme.breakpoints.down("sm")]: {
                                minHeight: "369px",
                              },
                            })}
                          >
                            <LockOpenIcon sx={{ fontSize: "2.5rem" }} />
                          </Card>
                          <Stack alignItems={"center"}>
                            <Card
                              sx={{
                                width: "2px",
                                height: "15px",
                                background: "var(--headerbg)",
                              }}
                            ></Card>
                            <Card
                              sx={{
                                width: "45%",
                                height: "15px",
                                borderTop: "2px solid var(--headerbg)",
                                borderLeft: "2px solid var(--headerbg)",
                                borderRight: "2px solid var(--headerbg)",
                                background: "transparent",
                                boxShadow: "none",
                              }}
                            ></Card>
                          </Stack>
                        </>
                      ) : item.State === "Locked" ? (
                        <>
                          <Card
                            sx={(theme) => ({
                              p: "20px",
                              background: theme.palette.secondary.lightest,
                              borderRadius: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              boxShadow: "none",
                              width: "100%",
                              flexDirection: "column",
                              minHeight: "285px",

                              [theme.breakpoints.down("sm")]: {
                                minHeight: "369px",
                              },
                            })}
                          >
                            <LockOutlineIcon sx={{ fontSize: "2.5rem" }} />
                          </Card>
                          <Stack alignItems={"center"}>
                            <Card
                              sx={{
                                width: "2px",
                                height: "15px",
                                background: "var(--headerbg)",
                              }}
                            ></Card>
                            <Card
                              sx={{
                                width: "45%",
                                height: "15px",
                                borderTop: "2px solid var(--headerbg)",
                                borderLeft: "2px solid var(--headerbg)",
                                borderRight: "2px solid var(--headerbg)",
                                background: "transparent",
                                boxShadow: "none",
                              }}
                            ></Card>
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Card
                            onClick={() => getUserTree(item?.PlacementName)}
                            sx={(theme) => ({
                              p: "20px",
                              background: theme.palette.secondary.lightest,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              cursor: "pointer",
                              boxShadow: "none",
                              width: "100%",
                              flexDirection: "column",
                            })}
                          >
                            <Stack
                              sx={{
                                position: "relative",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Paper
                                sx={(theme) => ({
                                  p: "5px",
                                  background: theme.palette.common.black,
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                })}
                              >
                                <Typography
                                  variant="body2"
                                  color="secondary.light"
                                >
                                  {item?.PlacementName}
                                </Typography>
                              </Paper>
                              <Paper
                                sx={{
                                  boxShadow: "none",
                                  background: "none",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  width: 70,
                                  height: 70,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid var(--secondary-main)",
                                }}
                              >
                                <img
                                  width={70}
                                  height={70}
                                  alt="profile-img"
                                  src={
                                    item.ProfilePicPath !== null
                                      ? item.ProfilePicPath
                                      : "https://byldstorageprod.blob.core.windows.net/byldcontainer-prod/backoffice-assets/default-user.png"
                                  }
                                  style={{
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Paper>
                            </Stack>
                            <Stack
                              sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                textAlign: "center",
                                background: "var(--black)",
                              }}
                            >
                              <Stack
                                textAlign={"center"}
                                sx={{ padding: "5px" }}
                              >
                                <Typography
                                  color="secondary.light"
                                  variant="body1"
                                  fontWeight={600}
                                  className="cm-tree-name"
                                >
                                  {item?.AcctName || "User Name"}
                                </Typography>
                                <Typography
                                  color="secondary.text"
                                  variant="body2"
                                >
                                  ({item?.AcctID || "----"})
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack width={"100%"}>
                              <Stack
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  textAlign: "center",
                                }}
                              >
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                    fontWeight={300}
                                    className="cm-placement-consult"
                                  >
                                    {item?.RankName}
                                  </Typography>
                                </Stack>
                              </Stack>
                              <Stack
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "row",
                                  textAlign: "center",
                                  justifyContent: "space-around",
                                }}
                              >
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.light"
                                    variant="h4"
                                    fontWeight={600}
                                  >
                                    {item?.LeftLegVol != null
                                      ? item?.LeftLegVol.toLocaleString("en-US")
                                      : "0"}
                                  </Typography>
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                  >
                                    {t("Dashboard.Left")}
                                  </Typography>
                                </Stack>
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.light"
                                    variant="h4"
                                    fontWeight={600}
                                  >
                                    {item?.RightLegVol != null
                                      ? item?.RightLegVol.toLocaleString(
                                          "en-US"
                                        )
                                      : "0"}
                                  </Typography>
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                  >
                                    {t("BinaryPlacementTree.Right")}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                          <Stack alignItems={"center"}>
                            <Card
                              sx={{
                                width: "2px",
                                height: "15px",
                                background: "var(--headerbg)",
                              }}
                            ></Card>
                            <Card
                              sx={{
                                width: "45%",
                                height: "15px",
                                borderTop: "2px solid var(--headerbg)",
                                borderLeft: "2px solid var(--headerbg)",
                                borderRight: "2px solid var(--headerbg)",
                                background: "transparent",
                                boxShadow: "none",
                              }}
                            ></Card>
                          </Stack>
                        </>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
              <Grid container spacing={2}>
                {state.thirdLevelData?.map((item) => {
                  return (
                    <Grid key={item.PlacementName} size={{ xs: 6, lg: 3 }}>
                      {item.State === "Open" ? (
                        <Card
                          sx={(theme) => ({
                            p: "20px",
                            background: theme.palette.secondary.lightest,
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            boxShadow: "none",
                            width: "100%",
                            flexDirection: "column",
                            minHeight: "285px",

                            [theme.breakpoints.down("sm")]: {
                              minHeight: "369px",
                            },
                          })}
                        >
                          <LockOpenIcon sx={{ fontSize: "2.5rem" }} />
                        </Card>
                      ) : item.State === "Locked" ? (
                        <Card
                          sx={(theme) => ({
                            p: "20px",
                            background: theme.palette.secondary.lightest,
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            boxShadow: "none",
                            width: "100%",
                            flexDirection: "column",
                            minHeight: "285px",
                          })}
                        >
                          <LockOutlineIcon sx={{ fontSize: "2.5rem" }} />
                        </Card>
                      ) : (
                        <>
                          <Card
                            onClick={() => getUserTree(item?.PlacementName)}
                            sx={(theme) => ({
                              p: "20px",
                              background: theme.palette.secondary.lightest,
                              borderRadius: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              boxShadow: "none",
                              width: "100%",
                              flexDirection: "column",
                            })}
                          >
                            <Stack
                              sx={{
                                position: "relative",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Paper
                                sx={(theme) => ({
                                  p: "5px",
                                  background: theme.palette.common.black,
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                })}
                              >
                                <Typography
                                  variant="body2"
                                  color="secondary.light"
                                >
                                  {item?.PlacementName}
                                </Typography>
                              </Paper>
                              <Paper
                                sx={{
                                  boxShadow: "none",
                                  background: "none",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  width: 70,
                                  height: 70,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid var(--secondary-main)",
                                }}
                              >
                                <img
                                  width={70}
                                  height={70}
                                  alt="profile-img"
                                  src={
                                    item.ProfilePicPath !== null
                                      ? item.ProfilePicPath
                                      : "https://byldstorageprod.blob.core.windows.net/byldcontainer-prod/backoffice-assets/default-user.png"
                                  }
                                  style={{
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Paper>
                            </Stack>
                            <Stack
                              sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                textAlign: "center",
                                background: "var(--black)",
                              }}
                            >
                              <Stack
                                textAlign={"center"}
                                sx={{ padding: "5px" }}
                              >
                                <Typography
                                  color="secondary.light"
                                  variant="body1"
                                  fontWeight={600}
                                  className="cm-tree-name"
                                >
                                  {item?.AcctName || "User Name"}
                                </Typography>
                                <Typography
                                  color="secondary.text"
                                  variant="body2"
                                >
                                  ({item?.AcctID || "----"})
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack width={"100%"}>
                              <Stack
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  textAlign: "center",
                                }}
                              >
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                    fontWeight={300}
                                    className="cm-placement-consult"
                                  >
                                    {item?.RankName}
                                  </Typography>
                                </Stack>
                              </Stack>
                              <Stack
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "row",
                                  textAlign: "center",
                                  justifyContent: "space-around",
                                }}
                              >
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.light"
                                    variant="h4"
                                    fontWeight={600}
                                  >
                                    {item?.LeftLegVol != null
                                      ? item?.LeftLegVol.toLocaleString("en-US")
                                      : "0"}
                                  </Typography>
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                  >
                                    {t("Dashboard.Left")}
                                  </Typography>
                                </Stack>
                                <Stack
                                  textAlign={"center"}
                                  sx={{ padding: "5px" }}
                                >
                                  <Typography
                                    color="secondary.light"
                                    variant="h4"
                                    fontWeight={600}
                                  >
                                    {item?.RightLegVol != null
                                      ? item?.RightLegVol.toLocaleString(
                                          "en-US"
                                        )
                                      : "0"}
                                  </Typography>
                                  <Typography
                                    color="secondary.text"
                                    variant="body2"
                                  >
                                    {t("BinaryPlacementTree.Right")}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                          <Stack alignItems={"center"} display={"none"}>
                            <Card
                              sx={{
                                width: "2px",
                                height: "15px",
                                background: "var(--headerbg)",
                              }}
                            ></Card>
                            <Card
                              sx={{
                                width: "45%",
                                height: "15px",
                                borderTop: "2px solid var(--headerbg)",
                                borderLeft: "2px solid var(--headerbg)",
                                borderRight: "2px solid var(--headerbg)",
                                background: "transparent",
                                boxShadow: "none",
                              }}
                            ></Card>
                          </Stack>
                          <Stack
                            display={"none"}
                            direction={"row"}
                            sx={{
                              justifyContent: "space-around",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{
                                px: 1,
                                py: 1,
                                margin: 0,
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                              startIcon={<AddIcon />}
                            />
                            <Button
                              variant="contained"
                              color="secondary"
                              sx={{
                                px: 1,
                                py: 1,
                                margin: 0,
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                              startIcon={<SouthIcon />}
                            />
                          </Stack>
                        </>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
              <Card
                sx={(theme) => ({
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  boxShadow: "none",
                  width: "100%",
                  mt: 2,
                  flexDirection: { xs: "column", sm: "row" },
                })}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => getBottomUserTree("L")}
                  startIcon={<SouthIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {t("BinaryPlacementTree.GoToBottomLeft")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => getBottomUserTree("R")}
                  startIcon={<SouthIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {t("BinaryPlacementTree.GoToBottomRight")}
                </Button>
              </Card>
            </Stack>
          </Stack>
        </Card>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  ) : (
    <>
      <Box width={"100%"}>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Stack gap={"20px"}>
            <Typography color="secondary.light" variant="h4" fontWeight={600}>
              {t("BinaryPlacementTree.BinaryPlacementTree")}
            </Typography>
          </Stack>
        </Card>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};
export default BinaryPlacementTree;